import { BugoData } from '@shared/api/bugo/bugo.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  BulkDeliveryAddress,
  GiftOrderCreateDto,
  GiftOrderDeliveryMode,
  GiftOrderItemCreateDto,
  GiftSelectedOption,
  GiftThanksPrintDto,
} from '@shared/api/giftOrder/giftOrder.interface';

import { calcOrderSurcharge } from './resolveDeliveryFee';

export interface OrderItemInput {
  giftItem: GiftItemForStore;
  quantity: number;
  selectedOptions?: GiftSelectedOption[];
}

/** 선택한 추가옵션 추가금 합계 */
const optionDeltaSum = (selectedOptions?: GiftSelectedOption[]): number =>
  (selectedOptions ?? []).reduce((sum, o) => sum + (o.priceDelta || 0), 0);

/** 주문 결제금액 계산 결과 — 화면 표시·DTO 양쪽에서 공용 */
export interface GiftOrderPayment {
  /** 상품금액(단가 = priceRetail + 옵션 추가금) 합계, 소량주문 추가금 제외 */
  goodsTotal: number;
  /** 소량주문 추가금 합계 */
  surchargeTotal: number;
  /** 최종 결제금액 = goodsTotal + surchargeTotal */
  paymentByCash: number;
  /** 과세 금액(부가세 포함) */
  taxableAmount: number;
  /** 면세 금액 */
  taxFreeAmount: number;
  /** 부가세 */
  vatAmount: number;
}

/**
 * 주문 결제금액 계산 (단가=priceRetail+옵션, 소량주문 추가금, 과세/면세/부가세).
 * 바로구매·장바구니 결제 모두 이 함수로 금액을 산출해 표시가와 청구가를 일치시킨다.
 */
export const calcGiftOrderPayment = (items: OrderItemInput[]): GiftOrderPayment => {
  // 단가 = priceRetail + Σ 선택옵션 추가금
  const unitPriceOf = (item: OrderItemInput) =>
    (item.giftItem.priceRetail || 0) + optionDeltaSum(item.selectedOptions);

  const goodsTotal = items.reduce(
    (sum, item) => sum + unitPriceOf(item) * item.quantity,
    0,
  );

  const taxableGoods = items.reduce((sum, item) => {
    const isTaxable = item.giftItem.giftItemInfo?.isTaxable ?? false;
    if (!isTaxable) return sum;
    return sum + unitPriceOf(item) * item.quantity;
  }, 0);

  // 소량주문 추가금 — 라인별 (개당 추가금 × 수량), 과세/면세 구분해 가산
  const surcharge = calcOrderSurcharge(
    items.map((item) => ({
      giftItemInfo: item.giftItem.giftItemInfo,
      quantity: item.quantity,
    })),
  );

  const taxableAmount = taxableGoods + surcharge.taxable;
  const paymentByCash = goodsTotal + surcharge.total;
  const taxFreeAmount = paymentByCash - taxableAmount;
  const vatAmount = Math.round(taxableAmount / 11);

  return {
    goodsTotal,
    surchargeTotal: surcharge.total,
    paymentByCash,
    taxableAmount,
    taxFreeAmount,
    vatAmount,
  };
};

interface IParams {
  bugo: BugoData;
  receiverName: string;
  receiverPhoneNumber: string;
  items: OrderItemInput[];
  deliveryMode?: GiftOrderDeliveryMode;
  bulkDeliveryAddress?: BulkDeliveryAddress;
  thanksPrint?: GiftThanksPrintDto;
}

/**
 * 다중 상품 GiftOrder DTO 생성 (백엔드 스펙 v2)
 */
export const getGiftOrderDto = ({
  bugo,
  receiverName,
  receiverPhoneNumber,
  items,
  deliveryMode,
  bulkDeliveryAddress,
  thanksPrint,
}: IParams): GiftOrderCreateDto => {
  const { paymentByCash, taxableAmount, taxFreeAmount, vatAmount } =
    calcGiftOrderPayment(items);

  const orderDetails: GiftOrderItemCreateDto[] = items.map((item) => {
    const isCouponType = item.giftItem.giftItemInfo?.type === GiftItemInfoType.Coupon;

    const orderDetail: GiftOrderItemCreateDto = {
      giftItem: item.giftItem._id,
      quantity: item.quantity,
    };

    if (item.selectedOptions && item.selectedOptions.length > 0) {
      orderDetail.selectedOptions = item.selectedOptions;
    }

    if (isCouponType) {
      orderDetail.couponDetail = { totalCount: item.quantity };
    }

    return orderDetail;
  });

  return {
    bugo: bugo._id,
    receiverName,
    receiverPhoneNumber,
    deliveryMode,
    bulkDeliveryAddress,
    orderDetails,
    thanksPrint,
    paymentDetail: {
      paymentByCash,
      taxFreeAmount,
      taxableAmount,
      vatAmount,
      paymentByPoint: 0,
    },
    webBaseUrl: process.env.REACT_APP_WEB_BASE_URL,
  };
};

/**
 * 단일 상품용 간편 DTO 생성 (바로구매용)
 */
export const getGiftOrderDtoSingle = ({
  giftItem,
  bugo,
  quantity,
  receiverName,
  receiverPhoneNumber,
  deliveryMode,
  bulkDeliveryAddress,
  selectedOptions,
  thanksPrint,
}: {
  giftItem: GiftItemForStore;
  bugo: BugoData;
  quantity: number;
  receiverName: string;
  receiverPhoneNumber: string;
  deliveryMode?: GiftOrderDeliveryMode;
  bulkDeliveryAddress?: BulkDeliveryAddress;
  selectedOptions?: GiftSelectedOption[];
  thanksPrint?: GiftThanksPrintDto;
}): GiftOrderCreateDto => {
  return getGiftOrderDto({
    bugo,
    receiverName,
    receiverPhoneNumber,
    deliveryMode,
    bulkDeliveryAddress,
    thanksPrint,
    items: [{ giftItem, quantity, selectedOptions }],
  });
};
